import React, { FC, useEffect, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { SearchIndex } from 'algoliasearch/dist/algoliasearch';

import { IconType, IdentifierDTO, Link, ZazumeModal } from '@zazume/zzm-base';

import { useCurrentOrganization } from '#/contexts/AuthProvider';
import { useSearchContext } from '#/contexts/SearchProvider';
import {
  Entities,
  filterExcludeObjectIds,
  fromIndexNameToAlgolia,
  GeographicalIndex,
  IndexName
} from '#/utils/algolia/algoliaHelper';

import { Input } from '../forms';
import { AlgoliaResultList } from './AlgoliaResultList';
import { StaticResultList } from './StaticResultList';
import { SuggestionList } from './SuggestionList';

const Container = styled.div`
  padding-top: 16px;
  padding-bottom: 16px;
`;

const Divider = styled.div(
  ({ theme }) => css`
    height: 1px;
    background-color: ${theme.colors.Gray200};
    margin: 16px 0;
  `
);

const ActionButtonSection = styled.div(
  ({ theme }) => css`
    padding-top: ${theme.spacing.s9};
  `
);

/**
 *
 * @param idsToExclude
 * @param filters Deprecated use idsToExclude instead. Only for back compatibility
 */
const toAlgoliaFilter = (idsToExclude?: IdentifierDTO[], filters?: string): string | undefined => {
  // Back compatibility remove when filters is not used anymore
  if (filters) {
    return filters;
  }

  if (idsToExclude) {
    return filterExcludeObjectIds(idsToExclude);
  }

  return undefined;
};

export interface AlgoliaSearcherProps {
  title?: string;
  placeholder?: string;
  entity: Entities;
  suggestedResultsHint?: string;
  onSuggestedResults?: any;
  onSelect: (selected: any) => void;
  hide: () => void;
  defaultResults?: any[];
  indexName: IndexName;
  /**
   * @deprecated use IdsToExclude instead
   */
  filters?: string;
  idsToExclude?: IdentifierDTO[];
  actionButtonText?: string;
  onClickActionButton?: () => void;
  actionButtonIcon?: IconType;
  actionButtonEnabled?: boolean;
  suffix?: string;
}

export const AlgoliaSearcher: FC<AlgoliaSearcherProps> = ({
  title,
  placeholder,
  entity,
  suggestedResultsHint,
  onSuggestedResults,
  onSelect,
  hide,
  defaultResults,
  indexName,
  idsToExclude,
  filters,
  actionButtonText,
  onClickActionButton,
  actionButtonIcon,
  actionButtonEnabled = false,
  suffix
}) => {
  const [name, setName] = useState<string>('');

  const organizationId = useCurrentOrganization();
  const { searchClient, searchAnonymousClient, indexPrefix } = useSearchContext();
  let index: SearchIndex | undefined;

  if (GeographicalIndex.includes(indexName as any)) {
    index = searchAnonymousClient?.initIndex(fromIndexNameToAlgolia(indexPrefix, suffix ?? organizationId)(indexName));
  } else {
    index = searchClient?.initIndex(fromIndexNameToAlgolia(indexPrefix, suffix ?? organizationId)(indexName));
  }

  const [typingTimeout, setTypingTimeout] = useState<any>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);

  useEffect(() => {
    return () => typingTimeout && clearTimeout(typingTimeout);
  });

  const startTimeout = (value: string) => {
    const toFilter = toAlgoliaFilter(idsToExclude, filters);
    return setTimeout(async () => {
      value !== name && setName(value);
      index &&
        index.search(value, { hitsPerPage: 3, filters: toFilter }).then(response => setSearchResults(response.hits));
      setTypingTimeout(null);
    }, 350);
  };

  const inputHandler = (value: string) => {
    typingTimeout && clearTimeout(typingTimeout);
    setTypingTimeout(startTimeout(value));
  };

  return (
    <ZazumeModal title={title}>
      <Container>
        <Input iconLeft="magnifier" onChange={inputHandler} placeholder={placeholder} />
        <Divider />
        {!defaultResults && name ? (
          <AlgoliaResultList indexName={indexName} selectAction={onSelect} results={searchResults} />
        ) : onSuggestedResults ? (
          <SuggestionList
            selectAction={onSelect}
            suggestedResultsHint={suggestedResultsHint}
            onSuggestedResults={onSuggestedResults}
            idsToExclude={idsToExclude}
            entity={entity}
          />
        ) : null}
        {defaultResults && (
          <StaticResultList name={name} defaultResults={defaultResults} selectAction={onSelect} entity={entity} />
        )}
        {actionButtonEnabled && (
          <ActionButtonSection>
            <Link variant="danger" onClick={onClickActionButton} leftIcon={actionButtonIcon}>
              {actionButtonText}
            </Link>
          </ActionButtonSection>
        )}
      </Container>
    </ZazumeModal>
  );
};
